import {EventEmitter2} from "../../util/EventEmitter2";
import {getLocalStorage, setLocalStorage} from "../../../../../../misc/LocalSession";

// the hardware sidebar's collapsed state is a per-browser preference shared by every
// opening, so it lives under one global key.
const HARDWARE_SIDEBAR_COLLAPSED_KEY = "elevationTool.hardwareSidebarCollapsed";

export class MdViewSettings {

    constructor() {
        this.hardwareSidebarCollapsed.subscribe(v => setLocalStorage(HARDWARE_SIDEBAR_COLLAPSED_KEY, v));
    }

    activeTab = new EventEmitter2<MdMenuTab>("workspace", "view.activeTab");
    activeView = new EventEmitter2<MdDisplayView>(DEFAULT_DISPLAY_VIEW, "view.activeView");
    dimShowAll = new EventEmitter2<boolean>(false, "view.dimShowAll");

    // which elevation the tool is focused on. Comes from the ?workspace= url param and
    // is written back to it when a ribbon tab changes it. Picks the ribbon tab's groups
    // and, with frameOnly, omits hardware + hinges from the drawing.
    workspace = new EventEmitter2<Workspace>(DEFAULT_WORKSPACE, "view.workspace");

    // true while the drawing shows the frame/screen only: the screen workspace on one of
    // its frame views. The "all" overview and the exports draw hardware + hinges too.
    frameOnly(): boolean {
        if(this.workspace.value !== "screen") return false;
        const view = this.activeView.value;
        return view === "frame-grid" || view === "frame-profile-active" || view === "frame-profile-inactive" || view === "top-down";
    }

    // hide the product-code labels on hardware crosshairs (frame + door). Set by
    // the screen export so the markers show without their labels; off everywhere
    // else. HardwareCrosshairThing reads this in draw().
    hideHardwareLabels = new EventEmitter2<boolean>(false, "view.hideHardwareLabels");

    // draw the reference mark ("P1") on each frame lite. Set by the exports: the
    // glazing submittal cites a lite by its mark, so it must be on the exported
    // drawing. Off in the editor. LiteFace reads this in draw().
    showLiteMarks = new EventEmitter2<boolean>(false, "view.showLiteMarks");

    // draw the frame's dimensions stacked outside the drawing (engineering-drawing
    // style) instead of inline. Set by the screen export. When on, the inline outer
    // + per-cell dimensions hide themselves and FrameGridView draws the stacked set
    // (see ExportFrameDimensions). Off everywhere else.
    externalDimensions = new EventEmitter2<boolean>(false, "view.externalDimensions");

    // restrict the stacked external dimensions to the outer-frame overall + door cell
    // overall + hardware offsets + hinge chain, suppressing the per-cell section sizes
    // and stick thicknesses. Set by the hardware export (a frame export focused on the
    // hardware). Only meaningful when externalDimensions is on; off everywhere else.
    hardwareOnlyDimensions = new EventEmitter2<boolean>(false, "view.hardwareOnlyDimensions");

    // "simple" (standard) vs "extended" dimensioning for the frame-profile /
    // top-down views. Bound to a ribbon checkbox; those views subscribe and map it
    // onto their dimensionMode. Defaults to true (matches the ChildView default).
    dimSimple = new EventEmitter2<boolean>(true, "view.dimSimple");

    // the elements currently selected on the canvas (sticks, lites, panels, …), or
    // an empty list when nothing is selected. Selecting an element auto-switches
    // activeTab to "selected" so its contextual ribbon tab shows (MS-Office style).
    // Multiple elements can be selected by ctrl/meta-clicking, subject to the group
    // rules in selectItem (only same-group elements coexist).
    selection = new EventEmitter2<MdSelection[]>([], "view.selection");

    // set while the ribbon waits for the user to click a stick on the canvas (e.g. the
    // second stick of a Split By). A stick tap calls onPick instead of selecting.
    stickPick = new EventEmitter2<MdStickPick | null>(null, "view.stickPick");

    // set while the user sets up frame anchors (see menubar/AnchorSetupTab). The ribbon
    // shows only that tab, and a stick tap adds or selects anchors instead of selecting
    // the stick.
    anchorSetup = new EventEmitter2<MdAnchorSetup | null>(null, "view.anchorSetup");

    // draw the frame anchors on the frame grid. Bound to a Screen tab checkbox. The exports
    // force it on.
    showAnchors = new EventEmitter2<boolean>(true, "view.showAnchors");

    // whether the hardware workspace's right sidebar (see components/HardwareSidebar) is
    // folded to its strip. Remembered across openings; the ribbon's Manage buttons unfold it.
    hardwareSidebarCollapsed = new EventEmitter2<boolean>(getLocalStorage(HARDWARE_SIDEBAR_COLLAPSED_KEY, false), "view.hardwareSidebarCollapsed");

    // whether the element with this id is part of the current selection. Canvas
    // items call this to keep their highlight in sync with the selection.
    isSelected(id: string): boolean {
        return this.selection.value.some(s => s.id === id);
    }

    // clear the canvas selection (drops the contextual tab). No-op when empty.
    clearSelection() {
        if(this.selection.value.length > 0) this.selection.emit([]);
    }

    // select an element from a canvas click. `additive` (ctrl/meta held) extends
    // the current selection when the element is group-compatible (see
    // selectionGroupOf) — toggling it out if already selected — and replaces the
    // selection when the group differs. A plain click always replaces.
    selectItem(item: MdSelection, additive: boolean) {
        const current = this.selection.value;

        // anchor setup selects only anchors: a tap on anything else clears the selection.
        if(this.anchorSetup.value && selectionGroupOf(item.type) !== "anchor") {
            this.clearSelection();
            return;
        }

        // plain click, nothing selected, or an incompatible group: replace.
        if(!additive || current.length === 0 || selectionGroupOf(current[0].type) !== selectionGroupOf(item.type)) {
            this.selection.emit([item]);
            return;
        }

        // ctrl-clicking an already-selected element removes it from the group.
        if(current.some(s => s.id === item.id)) {
            this.selection.emit(current.filter(s => s.id !== item.id));
            return;
        }

        this.selection.emit([...current, item]);
    }
}

// which mutually-exclusive selection group a type belongs to. Only elements of the
// same group may be multi-selected together: lites and panels ("cell") mix freely;
// sticks, door-lites and hardware-items each keep to their own group.
export type MdSelectionGroup = "cell" | "stick" | "door-lite" | "hardware-item" | "hinge" | "void" | "door-slot" | "anchor";

export function selectionGroupOf(type: MdSelection["type"]): MdSelectionGroup {
    switch(type) {
        case "lite":
        case "panel": return "cell";
        case "stick": return "stick";
        case "door-lite": return "door-lite";
        case "hardware-item": return "hardware-item";
        case "hinge": return "hinge";
        case "void": return "void";
        case "door-slot": return "door-slot";
        case "anchor-stick":
        case "anchor-section": return "anchor";
    }
}

// a pending stick pick: the stick the action started from, what to do with the stick
// the user clicks, and the ribbon tab's title and instructions while it waits.
export type MdStickPick = {
    stickId: string;
    onPick: (stickId: string) => void;
    title: string;
    instructions: string;
};

// the anchor setup mode: the product a tap on a stick with no anchors adds (null = none
// is set up, see ElevationApp.anchorProducts).
export type MdAnchorSetup = {
    paintProduct: number | null;
};

// the ribbon tab. "workspace" shows the focused workspace's tab (Screen / Door / Hardware);
// the Anchors tab is anchorSetup being set, and a stick pick replaces the strip.
export type MdMenuTab = "workspace" | "selected" | "debug" | "legacy";

// describes the canvas element backing the "Selected XX" contextual tab. Carries
// just enough to drive the tab's actions (BlockOps works off the model id): the
// stick's orientation picks its insert sides; a cell's resolved on-screen size
// lets a split divide the existing footprint.
export type MdSelection =
    | {type: "stick"; id: string; orientation: "vertical" | "horizontal"}
    | {type: "lite" | "panel"; id: string; size: {width: number; height: number}}
    | {type: "door-lite"; id: string; leaf: "active" | "inactive"}
    // a placed hardware-item crosshair (handle, lock, or any other placed item).
    // id is the MdHardwarePlacement id ("primaryHandle" / "primaryLock" /
    // "hardware:{productId}:{target}:{side}"); the differing door/frame ids let a
    // product's door and frame crosshairs be selected independently. `kind` here is
    // the crosshair's target (door/frame), used only for the tab label.
    | {type: "hardware-item"; id: string; kind: "door" | "frame"; productId: number}
    // a hinge on the Locations view. `id` is namespaced ("hinge:A") so it can't
    // collide with cell/stick ids in isSelected; `hingeId` ("A") looks the hinge up
    // in MdHinges.list.
    | {type: "hinge"; id: string; hingeId: string}
    // a void cell (a step in the frame outline).
    | {type: "void"; id: string}
    // a secondary door slot's cell (multi-opening screens). `id` is the doors item id.
    | {type: "door-slot"; id: string}
    // the anchors of an outline stick (anchor setup). `id` is namespaced ("anchor:stick-3")
    // so it can't collide with the stick's own selection id.
    | {type: "anchor-stick"; id: string; stickId: string}
    // one anchor section of an outline stick (anchor setup).
    | {type: "anchor-section"; id: string; stickId: string; index: number};

// the active "display" view shown on the canvas. Mirrors the scenes available
// under ./scenes (frameProfile/topDown/frameGrid/frameAll), plus the door
// elevation and locations views which are each parameterized by which leaf
// (active/inactive) they show.
export type MdDisplayView =
    | "frame-profile-active" | "frame-profile-inactive" | "top-down" | "frame-grid" | "all"
    | "door-active" | "door-inactive"
    | "location-active" | "location-inactive"
    // submittal export previews (Debug tab), see scenes/export.
    | "screen-export" | "door-export" | "hardware-export" | "cutplan-export";

// the view shown on load and returned to by the menubar Home button.
export const DEFAULT_DISPLAY_VIEW: MdDisplayView = "all";

// which of the three elevations the tool is focused on; one ribbon tab each.
export type Workspace = "screen" | "door" | "hardware";

export const DEFAULT_WORKSPACE: Workspace = "screen";

// the display views a workspace's ribbon tab offers.
export function workspaceViews(workspace: Workspace): MdDisplayView[] {
    switch(workspace) {
        case "screen": return ["all", "frame-grid", "frame-profile-active", "top-down"];
        case "door": return ["door-active"];
        case "hardware": return ["location-active"];
    }
}

// the view the tool lands on (and the Home button returns to) for a workspace.
export function workspaceHomeView(workspace: Workspace): MdDisplayView {
    switch(workspace) {
        case "door": return "door-active";
        case "hardware": return "location-active";
        case "screen": return "all";
    }
}
